import { useCallback, useEffect, useRef } from 'react'
import {
  useQuery as useQueryTanstack,
  UseQueryOptions as UseQueryOptionsTanstack,
  UseQueryResult as UseQueryResultTanstack,
  QueryObserverResult as QueryObserverResultTanstack,
  useQueryClient,
  QueryKey,
} from '@tanstack/react-query'
import type { MutationDataError } from '@/resources/types'

// eslint-disable-next-line import/export -- We need to shadow the useQuery function that is already exported from @tanstack/react-query
export * from '@tanstack/react-query'

// Override UseQueryResult to accept MutationDataError | Error
export type UseQueryResult<
  TData = unknown,
  TError extends Error | MutationDataError = Error | MutationDataError,
> = Omit<UseQueryResultTanstack<TData, TError>, 'error' | 'refetch'> & {
  error: TError | null
  refetch: (
    options?: Parameters<UseQueryResultTanstack<TData, TError>['refetch']>[0]
  ) => Promise<QueryObserverResult<TData, TError>>
  remove: () => void
}

// Override QueryObserverResult to accept MutationDataError | Error
export type QueryObserverResult<
  TData = unknown,
  TError extends Error | MutationDataError = Error | MutationDataError,
> = Omit<QueryObserverResultTanstack<TData, TError>, 'error' | 'refetch'> & {
  error: TError | null
  refetch: (
    options?: Parameters<
      QueryObserverResultTanstack<TData, TError>['refetch']
    >[0]
  ) => Promise<QueryObserverResult<TData, TError>>
}

export type UseQueryOptions<
  T = unknown,
  E extends Error | MutationDataError = Error | MutationDataError,
  TQueryKey extends QueryKey = QueryKey,
> = UseQueryOptionsTanstack<T, E, T, TQueryKey> & {
  /**
   * @deprecated
   * The `onSuccess` callback is deprecated.
   *
   * Instead of syncing state or side effects in `onSuccess`,
   * derive any data you need directly from the query result.
   *
   * For example:
   *
   * ```tsx
   * const { data } = useQuery({ queryKey, queryFn })
   * const derived = useMemo(() => transform(data), [data])
   * ```
   *
   * React Query’s state management already handles success transitions.
   * Running side effects in `onSuccess` can cause sync issues and unnecessary re-renders.
   *
   * See: https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
   */
  onSuccess?: (data: T) => void
  /**
   * @deprecated
   * The `onError` callback is deprecated.
   *
   * To handle errors, prefer using the `meta.errorMessage` option in your query:
   *
   * ```ts
   * useQuery({
   *   queryKey,
   *   queryFn,
   *   meta: {
   *     errorMessage: 'Failed to load user data',
   *   },
   * })
   * ```
   *
   * This keeps error handling consistent and declarative,
   * and allows for global error processing (e.g. toast notifications)
   * through React Query’s default `queryCache` error handlers.
   *
   * See: https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
   */
  onError?: (error: E) => void
}

export type UseQueryHookOptions<
  TData = unknown,
  E extends Error | MutationDataError = Error | MutationDataError,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<TData, E, TQueryKey>, 'queryKey' | 'queryFn'>

// eslint-disable-next-line import/export -- We need to shadow the useQuery function that is already exported from @tanstack/react-query
export const useQuery = <
  T = unknown,
  E extends Error | MutationDataError = Error | MutationDataError,
  TQueryKey extends QueryKey = QueryKey,
>({
  onSuccess,
  onError,
  ...options
}: UseQueryOptions<T, E, TQueryKey>) => {
  const queryClient = useQueryClient()
  const query = useQueryTanstack<T, E, T, TQueryKey>({ ...options })

  const successRef = useRef(onSuccess)
  const errorRef = useRef(onError)
  const prevDataRef = useRef<T>()
  const prevErrorRef = useRef<E>()

  successRef.current = onSuccess
  errorRef.current = onError

  const remove = useCallback(() => {
    queryClient.removeQueries({
      queryKey: options.queryKey,
    })
  }, [queryClient, options.queryKey])

  useEffect(() => {
    if (
      successRef.current &&
      query.isSuccess &&
      query.data !== prevDataRef.current
    ) {
      prevDataRef.current = query.data
      successRef.current(query.data)
    }
  }, [query.data, query.isSuccess])

  useEffect(() => {
    if (
      errorRef.current &&
      query.isError &&
      query.error !== prevErrorRef.current
    ) {
      prevErrorRef.current = query.error
      errorRef.current(query.error)
    }
  }, [query.error, query.isError])

  return { remove, ...query } as UseQueryResult<T, E>
}
